import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';

import { Metamodel } from 'app/model/base/model';
import { Owned } from 'app/model/base/named';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { isEmpty, merge } from 'lodash';
import { convertParametersToModel } from 'app/shared/utils/parameter-converter.util';
import {
    AzureSqlDatabaseLinkedService,
    IModel,
    linkedServiceReferenceToResource,
    WorkspaceBuildInLinkedService,
    isLogicAppExpression,
    Named,
    SharedModel,
    ConnectionKind,
    artifactsSupportDMTSCredential,
    artifactLinkedSerivceToKindMap,
    artifactLinkedSerivceType,
    ParameterConfig
} from 'shell-models-lib';
import { StoredProcedureActivity } from 'app/model/pipeline/storedprocedure.activity';
import { LinkedServiceAdapter } from '../linkedservices/linkedservice.adapter';
import { GlobalVariables } from 'app/services/globals';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import {
    ActivityConnectionContext,
    ISqlServerStoredProcedureActivityContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { findLinkedServiceFromContext, setDbFromLs } from 'app/shared/utils/update-pipeline-skill.util';
import { type ModelService } from 'app/model/services/model.service';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { LoggerService } from 'app/logger/logger.service';
import { IFrameService } from 'app/services/iframe.service';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(StoredProcedureActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        linkedService: '*',
        connectionSettings: '*',
        linkedServiceName: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            database: __IS_FABRIC_DPE__ ? '*' : undefined,
            storedProcedureName: '*',
            storedProcedureParameters: '*',
            enforceOneTimeExecution: '*',
            connectionProperties: __IS_FABRIC_DPE__ ? '*' : undefined
        },
        externalReferences: '*'
    }
})
export class StoredProcedureActivityAdapter extends ActivityAsyncAdapter<
    StoredProcedureActivity,
    Model.SqlServerStoredProcedureActivity,
    ISqlServerStoredProcedureActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(StoredProcedureActivity);
    }

    public override async toFabricCopilotContext(
        activity: StoredProcedureActivity
    ): Promise<ISqlServerStoredProcedureActivityContext> {
        if (activity) {
            const activityContext: ISqlServerStoredProcedureActivityContext = await super.toFabricCopilotContext(
                activity
            );
            if (activity.linkedService) {
                const lsAdapter = this.context.asyncAdapterFor<
                    LinkedService,
                    Model.LinkedServiceResource,
                    ActivityConnectionContext
                >(Metamodel.modelOf(activity.linkedService));
                activityContext.Connection = await lsAdapter?.toFabricCopilotContext(activity.linkedService);
                if (activityContext.Connection) {
                    activityContext.Connection.Database = ValueAdapter.toResource(
                        activity,
                        StoredProcedureActivity.DATABASE,
                        true
                    );
                    activityContext.ProcedureName = ValueAdapter.toResource(
                        activity,
                        StoredProcedureActivity.NAME,
                        true
                    );
                }
            }
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: ISqlServerStoredProcedureActivityContext,
        model?: StoredProcedureActivity
    ): Promise<StoredProcedureActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                model.linkedService = (
                    await findLinkedServiceFromContext(context.Connection, {
                        modelService,
                        iframeService
                    })
                ).linkedService;
                if (model.linkedService) {
                    const lsAdapter = Metamodel.isModel(context.Connection.Type)
                        ? this.context.asyncAdapterFor<
                            LinkedService,
                            Model.LinkedServiceResource,
                            ActivityConnectionContext
                        >(Metamodel.modelByName(context.Connection.Type))
                        : undefined;
                    if (lsAdapter) {
                        await lsAdapter.fromFabricCopilotContext(context.Connection, model.linkedService);
                        setDbFromLs(model, model.linkedService);
                    }
                }
                ValueAdapter.toModel(model, StoredProcedureActivity.DATABASE, context.Connection.Database);
                ValueAdapter.toModel(model, StoredProcedureActivity.NAME, context.ProcedureName);
            }
        }
        return model;
    }

    protected async _toResource(activity: StoredProcedureActivity): Promise<Model.SqlServerStoredProcedureActivity> {
        const modelStoredProcedureActivity: Model.SqlServerStoredProcedureActivity = super._getCommonActivityProperties(
            activity,
            'SqlServerStoredProcedure'
        ) as Model.SqlServerStoredProcedureActivity;
        const storedProcedureActivity: StoredProcedureActivity = activity as StoredProcedureActivity;
        modelStoredProcedureActivity.typeProperties = {
            storedProcedureName: ValueAdapter.toResource(storedProcedureActivity, StoredProcedureActivity.NAME),
            enforceOneTimeExecution:
                ((activity.linkedService as AzureSqlDatabaseLinkedService)?.sqlVersion === 'v2' || __IS_FABRIC_DPE__)
                    ? ValueAdapter.toResource(
                        storedProcedureActivity,
                        StoredProcedureActivity.ENFORCE_ONETIME_EXECUTION
                    )
                    : undefined
        };

        if (__IS_FABRIC_DPE__ || this.context.adapterConfig.convertToFabric) {
            modelStoredProcedureActivity.typeProperties.database =
                ValueAdapter.toResource(storedProcedureActivity, StoredProcedureActivity.DATABASE) || undefined;
        }

        modelStoredProcedureActivity.typeProperties.storedProcedureParameters =
            storedProcedureActivity.sprocParameters != null && storedProcedureActivity.sprocParameters.length > 0
                ? toParameterObject(storedProcedureActivity.sprocParameters, true)
                : ValueAdapter.toResource(storedProcedureActivity, StoredProcedureActivity.STORED_PROCEDURE_PARAMETERS);
        // clear [] to keep the current behavior
        if (
            modelStoredProcedureActivity.typeProperties.storedProcedureParameters &&
            Array.isArray(modelStoredProcedureActivity.typeProperties.storedProcedureParameters) &&
            modelStoredProcedureActivity.typeProperties.storedProcedureParameters.length === 0
        ) {
            modelStoredProcedureActivity.typeProperties.storedProcedureParameters = undefined;
        }
        if (activity.linkedService) {
            if (activity.linkedService.isWorkspaceBuiltIn) {
                const linkedServiceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(activity.linkedService));
                const linkedServiceResource = await linkedServiceAdapter.toResource(activity.linkedService);
                if (activity.dmtsCredentialForArtifact) {
                    linkedServiceResource.properties.linkedServiceName = linkedServiceReferenceToResource(
                        activity.dmtsCredentialForArtifact,
                        this.context
                    );
                }
                this._setArtifactLinkedService(
                    modelStoredProcedureActivity,
                    linkedServiceResource,
                    activity.dmtsCredentialForArtifact != null
                );

                if (linkedServiceResource?.properties) {
                    Reflect.deleteProperty(linkedServiceResource, 'objectId');
                }
            } else {
                modelStoredProcedureActivity.linkedServiceName = this._toLinkedServiceReferenceModel(
                    storedProcedureActivity.linkedService,
                    storedProcedureActivity.linkedServiceParameters
                );

                if (
                    __IS_FABRIC_DPE__ &&
                    storedProcedureActivity.linkedServiceType != null &&
                    Named.OBJECT_ID.templateValue(storedProcedureActivity.linkedService)
                ) {
                    modelStoredProcedureActivity.linkedServiceName.referenceType =
                        storedProcedureActivity.linkedServiceType;
                }
            }

            if (__IS_FABRIC_DPE__ && activity.linkedServiceProperties?.length > 0) {
                modelStoredProcedureActivity.typeProperties.connectionProperties = {
                    ...toParameterObject(activity.linkedServiceProperties, false, undefined, true)
                }
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            merge(modelStoredProcedureActivity, parsed);
        }

        return modelStoredProcedureActivity;
    }

    protected async _toModel(modelActivity: Model.SqlServerStoredProcedureActivity): Promise<StoredProcedureActivity> {
        const activity: StoredProcedureActivity = new StoredProcedureActivity(modelActivity.name);
        const modelStoredProcedureActivity = modelActivity as Model.SqlServerStoredProcedureActivity;
        const logger = await getServiceInstance<LoggerService>(this.context.contextData, 'LoggerService');

        this._updatedCommonProperties(modelStoredProcedureActivity, activity);

        if (modelStoredProcedureActivity.typeProperties) {
            ValueAdapter.toModel(
                activity,
                StoredProcedureActivity.NAME,
                modelStoredProcedureActivity.typeProperties.storedProcedureName
            );
            if (__IS_FABRIC_DPE__) {
                ValueAdapter.toModel(
                    activity,
                    StoredProcedureActivity.DATABASE,
                    modelStoredProcedureActivity.typeProperties.database
                );

                if (modelActivity.typeProperties.connectionProperties) {
                    activity.linkedServiceProperties = toParameterClass(
                        modelActivity.typeProperties.connectionProperties,
                        activity
                    ) as Array<ParameterConfig<string>>;
                }
            }
            if (
                modelStoredProcedureActivity.typeProperties.storedProcedureParameters != null &&
                typeof modelStoredProcedureActivity.typeProperties.storedProcedureParameters === 'object' &&
                modelStoredProcedureActivity.typeProperties.storedProcedureParameters.type === 'Expression'
            ) {
                ValueAdapter.toModel(
                    activity,
                    StoredProcedureActivity.STORED_PROCEDURE_PARAMETERS,
                    modelStoredProcedureActivity.typeProperties.storedProcedureParameters
                );
            } else {
                activity.sprocParameters = toParameterClass(
                    modelStoredProcedureActivity.typeProperties.storedProcedureParameters,
                    activity,
                    true
                );
            }

            ValueAdapter.toModel(
                activity,
                StoredProcedureActivity.ENFORCE_ONETIME_EXECUTION,
                modelStoredProcedureActivity.typeProperties.enforceOneTimeExecution
            );
        }

        const artifactLinkedServiceResource = this._getArtifactLinkedService(modelStoredProcedureActivity);
        let linkedServiceModel: IModel;
        let linkedServiceName: Model.LinkedServiceReference = modelStoredProcedureActivity.linkedServiceName;
        if (!linkedServiceName && __IS_FABRIC_DPE__ && !isEmpty(artifactLinkedServiceResource)) {
            const linkedServiceType = artifactLinkedServiceResource.properties?.type;

            // handle dmtsCredential
            if (linkedServiceType && artifactLinkedServiceResource.properties.linkedServiceName && __IS_FABRIC_DPE__) {
                const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                    this.context.contextData,
                    'LinkedServiceRegistryService'
                );
                const linkedServiceMetadata =
                    linkedServiceRegistryService.getLinkedServiceConfiguration(linkedServiceType);
                if (linkedServiceMetadata?.dmtsCredentialConnectionType) {
                    if (
                        isLogicAppExpression(artifactLinkedServiceResource.properties.linkedServiceName.referenceName)
                    ) {
                        const model = Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType);
                        if (model) {
                            const templateLinkedService = Owned.create<LinkedService>(model, activity);
                            templateLinkedService.name = artifactLinkedServiceResource.name || generateUUID().replaceAll('-', '_');
                            ValueAdapter.toModel(
                                templateLinkedService,
                                Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType).attribute(
                                    'objectId'
                                ),
                                artifactLinkedServiceResource.properties.linkedServiceName.referenceName
                            );
                            templateLinkedService.parameterized = true;
                            activity.dmtsCredentialForArtifact = templateLinkedService;
                        }
                        logger.log(
                            'stored-procedure-activity.adapter',
                            'artifact credential linkedService is parameterized',
                            {
                                linkedServiceType
                            }
                        );
                    } else {
                        activity.dmtsCredentialForArtifact = ReferenceAdapter.toProxyModel(
                            Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType),
                            artifactLinkedServiceResource.properties.linkedServiceName,
                            this.context
                        );
                    }
                }
            }

            if (this._shouldGetArtifactDMTSConnectionFromSolutionTemplate()) {
                const dmtsCredentialKey = `${modelActivity.name}/SqlServerStoredProcedure`;
                const dmtsCredentialForArtifact = this.context.adapterConfig.artifactDMTSCredentials.get(dmtsCredentialKey);
                if (dmtsCredentialForArtifact != null) {
                    activity.dmtsCredentialForArtifact = dmtsCredentialForArtifact;
                }
            }

            linkedServiceModel = Metamodel.modelByName(linkedServiceType);
            const linkedServiceAdapter = this.context.asyncAdapterFor(
                linkedServiceModel
            ) as LinkedServiceAdapter<LinkedService>;
            // Handle workspace built-in linked service in dynamic content
            const lsModel: WorkspaceBuildInLinkedService = Owned.create<WorkspaceBuildInLinkedService>(
                linkedServiceModel,
                activity
            );
            await linkedServiceAdapter?.toModel(
                {
                    ...artifactLinkedServiceResource,
                    properties: {
                        ...artifactLinkedServiceResource.properties,
                        linkedServiceName: undefined
                    }
                },
                lsModel
            );
            if (lsModel?.parameterized || activity?.dmtsCredentialForArtifact?.parameterized) {
                activity.linkedService = lsModel;
                if (lsModel?.parameterized) {
                    logger.log('stored-procedure-activity.adapter', 'linkedService is parameterized', {
                        linkedServiceType
                    });
                }
            } else {
                linkedServiceName = {
                    type: 'LinkedServiceReference',
                    referenceName: linkedServiceAdapter.getArtifactId(artifactLinkedServiceResource),
                    workspaceObjectId: linkedServiceAdapter.getWorkspaceObjectId(artifactLinkedServiceResource)
                };
            }
        }
        if (linkedServiceName != null) {
            if (
                __IS_FABRIC_DPE__ &&
                isLogicAppExpression((linkedServiceName as SharedModel.LinkedServiceReference).referenceName)
            ) {
                let linkedServiceType: string = modelStoredProcedureActivity.linkedServiceName.referenceType;
                activity.linkedServiceType = linkedServiceType;

                if (!linkedServiceType) {
                    logger.log(
                        'stored-procedure-activity.adapter',
                        'linkedService connection id is parameterized & cannot find related linkedService type',
                        { resourceName: modelStoredProcedureActivity.name }
                    );

                    const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                        this.context.contextData,
                        'LinkedServiceRegistryService'
                    );
                    linkedServiceType = linkedServiceRegistryService
                        .getAllLinkedServiceConfigurations()
                        .filter(
                            (metadata) =>
                                metadata.supportStoredProcedureActivity &&
                                !linkedServiceRegistryService.hidedByEc(metadata.type)
                        )[0]?.type;
                }

                if (linkedServiceType) {
                    const lsModel = Metamodel.modelByName(linkedServiceType);
                    const templateLinkedService = Owned.create<LinkedService>(lsModel, activity);
                    templateLinkedService.name = generateUUID().replaceAll('-', '_');
                    ValueAdapter.toModel(
                        templateLinkedService,
                        Metamodel.modelByName(linkedServiceType).attribute('objectId'),
                        (linkedServiceName as SharedModel.LinkedServiceReference).referenceName
                    );
                    templateLinkedService.tridentArtifactType =
                        templateLinkedService.tridentArtifactType ?? 'Connection';

                    const iframeService = await getServiceInstance<IFrameService>(
                        this.context.contextData,
                        'IFrameService'
                    );
                    templateLinkedService.tridentConnectionKind = (iframeService.currentCustomerProfile.linkedServiceToConnectionTypeMapping[linkedServiceType] || linkedServiceType) as ConnectionKind;
                    templateLinkedService.parameterized = true;
                    activity.linkedService = templateLinkedService;
                }

                logger.log('stored-procedure-activity.adapter', 'linkedService connection id is parameterized', {
                    linkedServiceType
                });
            } else {
                activity.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    linkedServiceModel ?? AzureSqlDatabaseLinkedService.MODEL,
                    linkedServiceName,
                    this.context
                );
            }

            if (linkedServiceName.parameters) {
                // Parameter value for linked service
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.linkedService,
                    activity.linkedServiceParameters,
                    linkedServiceName.parameters
                );
            }
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelStoredProcedureActivity, StoredProcedureActivity.MODEL)
        );

        return activity;
    }

    private _getArtifactLinkedService(resource: Model.SqlServerStoredProcedureActivity): Model.LinkedServiceResource {
        if (
            !GlobalVariables.enableConnectionSettings &&
            !artifactsSupportDMTSCredential.includes(
                artifactLinkedSerivceToKindMap[
                resource.connectionSettings?.properties?.type as artifactLinkedSerivceType
                ]
            )
        ) {
            return resource.linkedService;
        }
        return resource.connectionSettings ?? resource.linkedService;
    }

    private _setArtifactLinkedService(
        resource: Model.SqlServerStoredProcedureActivity,
        linkedService: Model.LinkedServiceResource,
        withDmtsCredential: boolean = false
    ): void {
        resource[
            (GlobalVariables.enableConnectionSettings ||
                artifactsSupportDMTSCredential.includes(
                    artifactLinkedSerivceToKindMap[linkedService?.properties?.type as artifactLinkedSerivceType]
                )) &&
                withDmtsCredential
                ? 'connectionSettings'
                : 'linkedService'
        ] = linkedService;
    }
}
